import { formatPhoneNumber } from '../utils/validation';
import React, { useState, useId } from 'react';

const colors = {
  darkTeal: '#416E72',
  teal: '#5B979A',
  lightestTeal: '#D9F0EE',
  nearBlack: '#181815',
  darkGray: '#353433',
  mediumGray: '#585752',
  lighterGray: '#E3E3DB',
  white: '#FFFFFF',
  darkGold: '#E0B004',
  error: '#c53030',
  errorLight: '#fed7d7',
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors.lighterGray,
  borderRadius: '6px',
  fontSize: '15px',
  fontFamily: 'Inter, sans-serif',
  color: colors.nearBlack,
  backgroundColor: colors.white,
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
  boxSizing: 'border-box',
};

const inputErrorStyle = {
  ...inputStyle,
  borderColor: colors.error,
  backgroundColor: colors.errorLight,
};

const inputFocusStyle = {
  borderColor: colors.teal,
  boxShadow: `0 0 0 3px ${colors.lightestTeal}`,
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: '500',
  fontSize: '14px',
  color: colors.darkGray,
};

const requiredStar = {
  color: colors.darkGold,
  marginLeft: '2px',
};

const optionalText = {
  color: colors.mediumGray,
  fontWeight: '400',
  fontSize: '12px',
  marginLeft: '4px',
};

const errorTextStyle = {
  color: colors.error,
  fontSize: '13px',
  fontFamily: 'Inter, sans-serif',
  marginTop: '4px',
};

const helpTextStyle = {
  color: colors.mediumGray,
  fontSize: '12px',
  fontFamily: 'Inter, sans-serif',
  marginTop: '4px',
  lineHeight: '1.4',
};

const radioGroupStyle = {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
};

const radioLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: colors.darkGray,
  padding: '8px 14px',
  borderRadius: '6px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors.lighterGray,
  transition: 'all 0.2s',
};

const radioLabelSelectedStyle = {
  ...radioLabelStyle,
  borderColor: colors.teal,
  backgroundColor: colors.lightestTeal,
};

export function HelpText({ children }) {
  return <div style={helpTextStyle}>{children}</div>;
}

export function Input({ label, required, optional, error, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <input
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          ...(focused && !hasError ? inputFocusStyle : {})
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        {...props}
      />
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

export function PhoneInput({ label, required, optional, error, value, onChange, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: formatted }
    };
    onChange(syntheticEvent);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <input
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        type="tel"
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          ...(focused && !hasError ? inputFocusStyle : {})
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        value={value}
        onChange={handleChange}
        placeholder="555-123-4567"
        maxLength={25}
        {...props}
      />
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

// Format ZIP code - numbers only, 5 digits max
function formatZipCode(value) {
  // Strip all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Limit to 5 digits
  return digits.slice(0, 5);
}

export function ZipInput({ label, required, optional, error, value, onChange, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleChange = (e) => {
    const formatted = formatZipCode(e.target.value);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: formatted }
    };
    onChange(syntheticEvent);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <input
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        type="text"
        inputMode="numeric"
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          ...(focused && !hasError ? inputFocusStyle : {})
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        value={value}
        onChange={handleChange}
        placeholder="12345"
        maxLength={5}
        {...props}
      />
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

// Format currency - only add commas, no $ (prefix is visual)
function formatCurrency(value) {
  // Strip all non-digit characters
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  // Convert to number and format with commas
  const number = parseInt(digits, 10);
  return number.toLocaleString('en-US');
}

export function CurrencyInput({ label, required, optional, error, value, onChange, placeholder = '0', helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: formatted }
    };
    onChange(syntheticEvent);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  const prefixStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 12px',
    backgroundColor: colors.lighterGray,
    borderRadius: '6px 0 0 6px',
    borderStyle: 'solid',
    borderWidth: '1px 0 1px 1px',
    borderColor: hasError ? colors.error : colors.lighterGray,
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    color: colors.mediumGray,
    fontWeight: '500',
  };

  const inputWithPrefixStyle = {
    ...(hasError ? inputErrorStyle : inputStyle),
    ...(focused && !hasError ? inputFocusStyle : {}),
    borderRadius: '0 6px 6px 0',
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <div style={{ display: 'flex' }}>
        <div style={prefixStyle}>$</div>
        <input
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
          type="text"
          inputMode="numeric"
          style={inputWithPrefixStyle}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          {...props}
        />
      </div>
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

// Format year - 4 digits only
function formatYear(value) {
  // Strip all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Limit to 4 digits
  return digits.slice(0, 4);
}

export function YearInput({ label, required, optional, error, value, onChange, placeholder = '2024', min, max, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleChange = (e) => {
    const formatted = formatYear(e.target.value);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: formatted }
    };
    onChange(syntheticEvent);
  };

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <input
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        type="text"
        inputMode="numeric"
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          ...(focused && !hasError ? inputFocusStyle : {})
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        maxLength={4}
        {...props}
      />
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

export function Select({ label, required, optional, options, placeholder = 'Select...', error, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <select
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          ...(focused && !hasError ? inputFocusStyle : {}),
          cursor: 'pointer'
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map(opt => {
          const optValue = typeof opt === 'object' ? opt.value : opt;
          const optLabel = typeof opt === 'object' ? opt.label : opt;
          return (
            <option key={optValue} value={optValue}>{optLabel}</option>
          );
        })}
      </select>
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

export function TextArea({ label, required, optional, error, helpText, validate, onBlur, ...props }) {
  const generatedId = useId();
  const inputId = props.id || generatedId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [localError, setLocalError] = useState(null);

  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (validate) {
      const validationError = validate(e.target.value);
      setLocalError(validationError);
    }
    if (onBlur) onBlur(e);
  };

  const displayError = touched ? (error || localError) : error;
  const hasError = Boolean(displayError);

  return (
    <div style={{ marginBottom: '16px' }}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </label>
      <textarea
        id={inputId}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${inputId}-error` : undefined}
        style={{
          ...(hasError ? inputErrorStyle : inputStyle),
          minHeight: '80px',
          resize: 'vertical',
          ...(focused && !hasError ? inputFocusStyle : {})
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        {...props}
      />
      {displayError && <div id={`${inputId}-error`} style={errorTextStyle}>{displayError}</div>}
      {helpText && !displayError && <HelpText>{helpText}</HelpText>}
    </div>
  );
}

export function RadioGroup({ label, required, optional, options, value, onChange, error, name }) {
  const instanceId = useId();
  const groupName = name || `radio-${instanceId}`;

  return (
    <div style={{ marginBottom: '16px' }} role="radiogroup" aria-invalid={Boolean(error) || undefined} aria-labelledby={`${groupName}-label`}>
      <div id={`${groupName}-label`} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </div>
      <div style={radioGroupStyle}>
        {options.map((opt, idx) => {
          const optionId = `${groupName}-${idx}`;
          return (
            <label
              key={opt}
              htmlFor={optionId}
              style={value === opt ? radioLabelSelectedStyle : radioLabelStyle}
            >
              <input
                type="radio"
                id={optionId}
                name={groupName}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                aria-label={opt}
                style={{ accentColor: colors.darkTeal }}
              />
              {opt}
            </label>
          );
        })}
      </div>
      {error && <div style={errorTextStyle} role="alert">{error}</div>}
    </div>
  );
}

export function CheckboxGroup({ label, required, optional, options, values, onToggle, error, name }) {
  const groupName = name || `checkbox-${(label || 'group').replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div style={{ marginBottom: '16px' }} role="group" aria-labelledby={`${groupName}-label`}>
      <div id={`${groupName}-label`} style={labelStyle}>
        {label}
        {required && <span style={requiredStar}>*</span>}
        {optional && <span style={optionalText}>(optional)</span>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {options.map((opt, idx) => {
          const optionId = `${groupName}-${idx}`;
          return (
            <label
              key={opt}
              htmlFor={optionId}
              style={values.includes(opt) ? radioLabelSelectedStyle : radioLabelStyle}
            >
              <input
                type="checkbox"
                id={optionId}
                name={groupName}
                value={opt}
                checked={values.includes(opt)}
                onChange={() => onToggle(opt)}
                aria-label={opt}
                style={{ accentColor: colors.darkTeal }}
              />
              {opt}
            </label>
          );
        })}
      </div>
      {error && <div style={errorTextStyle} role="alert">{error}</div>}
    </div>
  );
}
